import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import WorkFormPage from '../../../components/work/WorkFormPage';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkSelectWithManage from '../../../components/work/WorkSelectWithManage';
import {
  projectsApi,
  projectCategoriesApi,
  projectSubcategoriesApi,
  projectTemplatesApi,
} from '../../../utils/workApi';
import { buildProjectPayload } from '../../../utils/workPayloadBuilders';
import { getProjectTemplateFromResponse } from '../../../utils/workResponseUtils';
import WorkCheckbox from '../../../components/work/WorkCheckbox';
import { show422Error } from '../../../utils/api';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';
import {
  mergeBreadcrumbIntoNavigationState,
  trailWithoutLastCrumb,
} from '../../../utils/breadcrumbTrail';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  Row,
  Col,
  message,
  InputNumber,
  Switch,
  Spin,
} from 'antd';
import { ProjectOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import { toJalali } from '../../../utils/dateUtils';
import { stripHtml } from '../../../utils/stripHtml';

const { TextArea } = Input;
const { Option } = Select;

const PROJECT_STATUS_OPTIONS = [
  { value: 'not started', label: 'شروع نشده' },
  { value: 'in progress', label: 'در حال انجام' },
  { value: 'on hold', label: 'متوقف شده' },
  { value: 'canceled', label: 'لغو شده' },
  { value: 'finished', label: 'تکمیل شده' },
];

const normalizeProjectStatus = (status) => {
  if (!status) return 'in progress';
  const map = {
    'شروع نشده': 'not started',
    'در حال انجام': 'in progress',
    'متوقف شده': 'on hold',
    'لغو شده': 'canceled',
    'تکمیل شده': 'finished',
    'not started': 'not started',
    'in progress': 'in progress',
    'on hold': 'on hold',
    canceled: 'canceled',
    finished: 'finished',
  };
  return map[status] || status;
};

/** team_id واقعی — نه id رکورد pivot */
const extractProjectTeamIds = (project, catalog = []) => {
  if (Array.isArray(project?.team_id) && project.team_id.length) {
    return project.team_id.map(Number).filter((n) => !Number.isNaN(n));
  }
  if (project?.team_id != null && project.team_id !== '') {
    return [Number(project.team_id)].filter((n) => !Number.isNaN(n));
  }
  const catalogIds = new Set((catalog || []).map((d) => Number(d.id)));
  return (project?.departments || project?.teams || [])
    .map((d) => {
      if (d == null) return null;
      if (typeof d === 'number' || typeof d === 'string') return Number(d);
      // ساختار جدید: { id: pivotId, department: { id, team_name } }
      if (d.department?.id != null) return Number(d.department.id);
      if (d.team?.id != null) return Number(d.team.id);
      if (d.team_id != null) return Number(d.team_id);
      if (d.department_id != null) return Number(d.department_id);
      if (d.pivot?.team_id != null) return Number(d.pivot.team_id);
      if (d.team_name != null && d.id != null) return Number(d.id);
      if (d.id != null && catalogIds.has(Number(d.id))) return Number(d.id);
      return null;
    })
    .filter((id) => id != null && !Number.isNaN(id));
};

const mergeDepartmentOptions = (catalog = [], projectDepartments = []) => {
  const merged = [...(catalog || [])];
  const pushTeam = (team) => {
    if (!team?.id) return;
    if (!merged.some((x) => Number(x.id) === Number(team.id))) {
      merged.push(team);
    }
  };
  (projectDepartments || []).forEach((d) => {
    if (d?.department?.id) pushTeam(d.department);
    else if (d?.team?.id) pushTeam(d.team);
    else if (d?.team_name) pushTeam(d);
  });
  return merged;
};

/** اعضای فعلی پروژه که در لیست employees نیستند را اضافه می‌کند تا نام (نه id) دیده شود */
const mergeEmployeeOptions = (catalog = [], members = []) => {
  const merged = (catalog || []).map((e) => ({ ...e, id: Number(e.id) }));
  (members || []).forEach((m) => {
    const u = m.user;
    const uid = Number(u?.id ?? m.user_id);
    if (!uid || Number.isNaN(uid)) return;
    if (!merged.some((x) => Number(x.id) === uid)) {
      merged.push({
        id: uid,
        name: u?.name || String(uid),
        email: u?.email,
        image_url: u?.image_url,
      });
    }
  });
  return merged;
};

const extractMemberUserIds = (members = []) =>
  (members || [])
    .map((m) => Number(m.user?.id ?? m.user_id))
    .filter((n) => n && !Number.isNaN(n));

const ProjectForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();
  const presetTemplateId = location.state?.templateId;
  const copyFromId = location.state?.copyFromId;
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/work/projects', { state: buildGoBackState() });
  const { canAdd, canEdit } = useResourcePermissions('projects');
  const [form] = Form.useForm();
  const projectNameWatch = Form.useWatch('project_name', form);
  const entityLabel = isEdit
    ? projectNameWatch || `ویرایش پروژه #${id}`
    : copyFromId
      ? 'ایجاد نسخه مشابه پروژه'
      : presetTemplateId
        ? 'ایجاد پروژه از الگو'
        : 'پروژه جدید';
  const { trail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/projects',
    listLabel: 'پروژه‌ها',
    entityLabel,
  });
  const goToProjectView = (projectId = id) => {
    if (!projectId) {
      goBackToList();
      return;
    }
    navigate(`/work/projects/${projectId}`, {
      state: {
        returnTo: returnToRef.current || '/work/projects',
        ...buildGoBackState(),
        ...mergeBreadcrumbIntoNavigationState({}, trailWithoutLastCrumb(trail)),
      },
    });
  };
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [options, setOptions] = useState({
    clients: [],
    employees: [],
    departments: [],
    currencies: [],
  });
  const [catModal, setCatModal] = useState(false);
  const [subModal, setSubModal] = useState(false);
  const [catModalOpening, setCatModalOpening] = useState(false);
  const [subModalOpening, setSubModalOpening] = useState(false);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const selectedCategory = Form.useWatch('category_id', form);
  const withoutDeadline = Form.useWatch('without_deadline', form);

  const refreshCategories = async () => {
    const res = await projectCategoriesApi.getCreateData();
    setCategories(res?.categories || []);
    return res?.categories || [];
  };

  const loadSubcategories = async (categoryId, { showLoading = false } = {}) => {
    if (!categoryId) {
      setSubcategories([]);
      return;
    }
    if (showLoading) setSubcategoriesLoading(true);
    try {
      const res = await projectSubcategoriesApi.getByCategory(categoryId);
      setSubcategories(res?.sub_categories || res?.subcategories || res?.data || []);
    } finally {
      if (showLoading) setSubcategoriesLoading(false);
    }
  };

  const applyTemplateToForm = async (templateId, createRes) => {
    if (!templateId) return;
    setApplyingTemplate(true);
    try {
      let tpl = createRes?.project_template || null;
      let members = createRes?.project_template_members || [];
      if (!tpl) {
        const tplRes = await projectTemplatesApi.getById(templateId);
        tpl = getProjectTemplateFromResponse(tplRes);
        members = tpl?.members || [];
      }
      if (!tpl) return;

      const memberUserIds = (members || [])
        .map((m) => m.user?.id || m.user_id)
        .filter(Boolean);

      if (tpl.category_id) {
        await loadSubcategories(tpl.category_id);
      }

      form.setFieldsValue({
        template_id: Number(templateId),
        project_name: tpl.project_name,
        project_summary: tpl.project_summary,
        notes: tpl.notes,
        category_id: tpl.category_id,
        sub_category_id: tpl.sub_category_id,
        user_id: memberUserIds.length ? memberUserIds : undefined,
        manual_timelog: tpl.manual_timelog === 'enable' || tpl.manual_timelog === 'on',
      });
    } catch {
      message.error('خطا در بارگذاری الگوی پروژه');
    } finally {
      setApplyingTemplate(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = isEdit
          ? await projectsApi.getEditData(id)
          : await projectsApi.getCreateData();
        const cats = res?.categories || [];
        setCategories(cats);
        if (!isEdit) {
          setOptions({
            clients: res?.clients || [],
            employees: res?.employees || [],
            departments: res?.departments || [],
            currencies: res?.currencies || [],
          });
          // تا زمان آماده شدن endpoint ایجاد از الگو، فقط template_id را نگه می‌داریم
          if (presetTemplateId) {
            form.setFieldsValue({ template_id: Number(presetTemplateId) });
          }
          if (copyFromId) {
            try {
              const copyRes = await projectsApi.getEditData(copyFromId);
              const p = copyRes?.project;
              if (p) {
                const catalogDepts = res?.departments || [];
                const teamIds = extractProjectTeamIds(p, catalogDepts);
                const mergedDepartments = mergeDepartmentOptions(catalogDepts, p.departments || []);
                const employees = mergeEmployeeOptions(
                  copyRes?.employees?.length ? copyRes.employees : res?.employees || [],
                  p.members || [],
                );
                setOptions((prev) => ({
                  ...prev,
                  departments: mergedDepartments,
                  clients: copyRes?.clients?.length ? copyRes.clients : prev.clients,
                  employees,
                  currencies: copyRes?.currencies?.length ? copyRes.currencies : prev.currencies,
                }));
                form.setFieldsValue({
                  project_name: p.project_name ? `${p.project_name} (کپی)` : undefined,
                  project_summary: stripHtml(p.project_summary) || '',
                  client_id: p.client_id != null ? Number(p.client_id) : undefined,
                  category_id: p.category_id != null ? Number(p.category_id) : undefined,
                  sub_category_id: p.sub_category_id != null ? Number(p.sub_category_id) : undefined,
                  team_id: teamIds,
                  start_date: p.start_date ? toJalali(p.start_date) : undefined,
                  deadline: p.deadline ? toJalali(p.deadline) : undefined,
                  without_deadline: !p.deadline,
                  notes: stripHtml(p.notes) || '',
                  currency_id: p.currency_id != null ? Number(p.currency_id) : undefined,
                  project_budget: p.project_budget,
                  hours_allocated: p.hours_allocated,
                  user_id: extractMemberUserIds(p.members),
                  public: Number(p.public) === 1 || p.public === 'on' || p.public === true,
                  public_gantt_chart: p.public_gantt_chart === 'enable' ? 'enable' : 'disable',
                  public_taskboard: p.public_taskboard === 'enable' ? 'enable' : 'disable',
                  need_approval_by_admin: Number(p.need_approval_by_admin) === 1,
                  manual_timelog: p.manual_timelog === 'enable' || p.manual_timelog === 'on',
                  client_task_notification:
                    p.allow_client_notification === 'enable' || p.client_view_task === 'enable',
                });
                if (p.category_id) await loadSubcategories(p.category_id);
              }
            } catch {
              message.warning('کپی پروژه بارگذاری نشد؛ فرم خالی باز شد');
            }
          }
        }
        if (isEdit && res?.project) {
          const p = res.project;
          const catalogDepts = res?.departments || [];
          const teamIds = extractProjectTeamIds(p, catalogDepts);
          const mergedDepartments = mergeDepartmentOptions(catalogDepts, p.departments || []);
          setOptions({
            clients: res?.clients || [],
            employees: mergeEmployeeOptions(res?.employees || [], p.members || []),
            departments: mergedDepartments,
            currencies: res?.currencies || [],
          });
          form.setFieldsValue({
            project_code: p.project_short_code || p.project_code || undefined,
            project_name: p.project_name,
            project_summary: stripHtml(p.project_summary) || '',
            client_id: p.client_id != null ? Number(p.client_id) : undefined,
            category_id: p.category_id != null ? Number(p.category_id) : undefined,
            sub_category_id: p.sub_category_id != null ? Number(p.sub_category_id) : undefined,
            team_id: teamIds,
            start_date: p.start_date ? toJalali(p.start_date) : undefined,
            deadline: p.deadline ? toJalali(p.deadline) : undefined,
            without_deadline: !p.deadline,
            status: normalizeProjectStatus(p.status),
            notes: stripHtml(p.notes) || '',
            currency_id: p.currency_id != null ? Number(p.currency_id) : undefined,
            project_budget: p.project_budget,
            hours_allocated: p.hours_allocated,
            user_id: extractMemberUserIds(p.members),
            public: Number(p.public) === 1 || p.public === 'on' || p.public === true,
            public_gantt_chart: p.public_gantt_chart === 'enable' ? 'enable' : 'disable',
            public_taskboard: p.public_taskboard === 'enable' ? 'enable' : 'disable',
            need_approval_by_admin: Number(p.need_approval_by_admin) === 1,
            manual_timelog: p.manual_timelog === 'enable' || p.manual_timelog === 'on',
            client_task_notification:
              p.allow_client_notification === 'enable' || p.client_view_task === 'enable',
          });
          if (p.category_id) await loadSubcategories(p.category_id);
        }
      } catch {
        message.error('خطا در بارگذاری فرم');
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, form, presetTemplateId, copyFromId]);

  useEffect(() => {
    if (!fetching) {
      if (isEdit && !canEdit) navigate('/work/projects', { replace: true });
      if (!isEdit && !canAdd) navigate('/work/projects', { replace: true });
    }
  }, [fetching, isEdit, canAdd, canEdit, navigate]);

  const buildPayload = (values) =>
    buildProjectPayload(values, { projectId: id, isEdit });

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      const body = buildPayload(values);
      if (isEdit) {
        await projectsApi.update(id, body);
        message.success('پروژه به‌روز شد');
        goToProjectView(id);
      } else {
        const res = await projectsApi.create(body);
        const newId = res?.project?.id || res?.data?.project?.id;
        message.success('پروژه ایجاد شد');
        if (newId) goToProjectView(newId);
        else goBackToList();
      }
    } catch (e) {
      show422Error(e, form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WorkFormPage
      title={
        isEdit
          ? 'ویرایش پروژه'
          : copyFromId
            ? 'ایجاد نسخه مشابه پروژه'
            : presetTemplateId
              ? 'ایجاد پروژه از الگو'
              : 'پروژه جدید'
      }
      subtitle="اطلاعات پروژه را تکمیل کنید"
      icon={ProjectOutlined}
      iconClassName="bg-sky-50 dark:bg-sky-900/20"
      iconColorClass="text-sky-600 dark:text-sky-400"
      backPath={isEdit ? `/work/projects/${id}` : '/work/projects'}
      onBack={isEdit ? () => goToProjectView(id) : goBackToList}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
    >
      {fetching ? (
        <FormSkeleton rows={10} columns={2} />
      ) : (
      <Card className="shadow-lg border-0 bg-white dark:bg-gray-800">
        <Spin spinning={applyingTemplate} tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری الگو...</span>}>
        <Form
          form={form}
          layout="vertical"
          className="work-panel-form"
          onFinish={onFinish}
          onFinishFailed={() => scrollToFirstError(form)}
          scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
          initialValues={{
            public_gantt_chart: 'disable',
            public_taskboard: 'disable',
          }}
          style={{ fontFamily: 'CustomFont' }}
        >
          <Form.Item name="template_id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="project_code" label={<span style={{ fontFamily: 'CustomFont' }}>کد پروژه</span>}>
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                name="project_name"
                label={<span style={{ fontFamily: 'CustomFont' }}>نام پروژه</span>}
                rules={[{ required: true, message: 'نام پروژه الزامی است' }]}
              >
                <Input style={{ fontFamily: 'CustomFont' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="start_date"
                label={<span style={{ fontFamily: 'CustomFont' }}>تاریخ شروع</span>}
                rules={[{ required: true, message: 'تاریخ شروع الزامی است' }]}
              >
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="deadline"
                label={<span style={{ fontFamily: 'CustomFont' }}>موعد نهایی</span>}
                dependencies={['without_deadline']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue('without_deadline') || value) return Promise.resolve();
                      return Promise.reject(new Error('موعد نهایی الزامی است'));
                    },
                  }),
                ]}
              >
                <PersianDatePicker className="w-full" disabled={withoutDeadline} />
              </Form.Item>
              <Form.Item name="without_deadline" valuePropName="checked" className="mb-0">
                <WorkCheckbox
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      form.setFieldValue('deadline', undefined);
                      form.validateFields(['deadline']).catch(() => {});
                    }
                  }}
                >
                  بدون موعد نهایی
                </WorkCheckbox>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="client_id" label={<span style={{ fontFamily: 'CustomFont' }}>مشتری</span>}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={(options.clients || []).map((c) => ({
                    value: Number(c.id),
                    label: c.name,
                  }))}
                  style={{ fontFamily: 'CustomFont' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <WorkSelectWithManage
                label="دسته‌بندی"
                name="category_id"
                onManageClick={async () => {
                  setCatModalOpening(true);
                  try {
                    await refreshCategories();
                    setCatModal(true);
                  } finally {
                    setCatModalOpening(false);
                  }
                }}
                manageLoading={catModalOpening}
                selectProps={{
                  allowClear: true,
                  showSearch: true,
                  optionFilterProp: 'label',
                  onChange: (v) => {
                    form.setFieldValue('sub_category_id', undefined);
                    loadSubcategories(v, { showLoading: true });
                  },
                }}
              >
                {categories.map((c) => (
                  <Option key={c.id} value={Number(c.id)} label={c.category_name}>
                    {c.category_name}
                  </Option>
                ))}
              </WorkSelectWithManage>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={24}>
              <Spin spinning={subcategoriesLoading} tip={<span style={{ fontFamily: 'CustomFont' }}>در حال بارگذاری زیردسته...</span>}>
                <WorkSelectWithManage
                  label="زیردسته"
                  name="sub_category_id"
                  onManageClick={async () => {
                    if (!selectedCategory) {
                      message.warning('ابتدا دسته را انتخاب کنید');
                      return;
                    }
                    setSubModalOpening(true);
                    try {
                      await loadSubcategories(selectedCategory);
                      setSubModal(true);
                    } finally {
                      setSubModalOpening(false);
                    }
                  }}
                  manageLoading={subModalOpening}
                  selectProps={{
                    allowClear: true,
                    disabled: !selectedCategory || subcategoriesLoading,
                    loading: subcategoriesLoading,
                  }}
                >
                  {subcategories.map((s) => (
                    <Option key={s.id} value={Number(s.id)}>
                      {s.category_name}
                    </Option>
                  ))}
                </WorkSelectWithManage>
              </Spin>
            </Col>
          </Row>
          <Form.Item name="team_id" label={<span style={{ fontFamily: 'CustomFont' }}>تیم / دپارتمان</span>}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={(options.departments || []).map((d) => ({
                value: Number(d.id),
                label: d.team_name,
              }))}
              style={{ fontFamily: 'CustomFont' }}
            />
          </Form.Item>
          <Form.Item name="user_id" label={<span style={{ fontFamily: 'CustomFont' }}>اعضا</span>}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              options={(options.employees || []).map((e) => ({
                value: Number(e.id),
                label: e.name,
              }))}
              style={{ fontFamily: 'CustomFont' }}
            />
          </Form.Item>
          {isEdit ? (
            <Form.Item name="status" label={<span style={{ fontFamily: 'CustomFont' }}>وضعیت</span>}>
              <Select
                options={PROJECT_STATUS_OPTIONS}
                style={{ fontFamily: 'CustomFont' }}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="project_summary" label={<span style={{ fontFamily: 'CustomFont' }}>خلاصه</span>}>
            <TextArea rows={3} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="notes" label={<span style={{ fontFamily: 'CustomFont' }}>یادداشت</span>}>
            <TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="currency_id" label={<span style={{ fontFamily: 'CustomFont' }}>ارز</span>}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={(options.currencies || []).map((c) => ({
                    value: Number(c.id),
                    label: c.currency_name,
                  }))}
                  style={{ fontFamily: 'CustomFont' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="project_budget" label={<span style={{ fontFamily: 'CustomFont' }}>بودجه</span>}>
                <WorkPriceInput />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="hours_allocated"
                label={<span style={{ fontFamily: 'CustomFont' }}>ساعات برآورد شده</span>}
              >
                <InputNumber className="w-full" min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="public" valuePropName="checked" className="mb-0 mt-2">
                <WorkCheckbox>پروژه عمومی</WorkCheckbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="public_gantt_chart" label={<span style={{ fontFamily: 'CustomFont' }}>گانت عمومی</span>}>
                <Select style={{ fontFamily: 'CustomFont' }}>
                  <Option value="enable">فعال</Option>
                  <Option value="disable">غیرفعال</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="public_taskboard" label={<span style={{ fontFamily: 'CustomFont' }}>برد عمومی</span>}>
                <Select style={{ fontFamily: 'CustomFont' }}>
                  <Option value="enable">فعال</Option>
                  <Option value="disable">غیرفعال</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="need_approval_by_admin" valuePropName="checked" className="mb-0 mt-2">
                <WorkCheckbox>نیاز به تأیید ادمین</WorkCheckbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="manual_timelog" valuePropName="checked">
                <Switch /> <span style={{ fontFamily: 'CustomFont', marginRight: 8 }}>ثبت دستی زمان</span>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="client_task_notification" valuePropName="checked">
                <Switch /> <span style={{ fontFamily: 'CustomFont', marginRight: 8 }}>اعلان وظیفه به مشتری</span>
              </Form.Item>
            </Col>
          </Row>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              onClick={() => (isEdit ? goToProjectView(id) : goBackToList())}
              style={{ fontFamily: 'CustomFont' }}
            >
              انصراف
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
              className={PANEL_PRIMARY_BTN_CLASS}
              style={{ fontFamily: 'CustomFont' }}
            >
              ذخیره
            </Button>
          </div>
        </Form>
        </Spin>
      </Card>
      )}

      <WorkManageEntityModal
        open={catModal}
        onClose={async () => {
          setCatModal(false);
          await refreshCategories();
        }}
        title="دسته‌بندی پروژه"
        nameField="category_name"
        nameLabel="نام دسته"
        loadItems={refreshCategories}
        onCreate={(name) => projectCategoriesApi.create(name)}
        onUpdate={(id, name) => projectCategoriesApi.update(id, name)}
        onDelete={(cid) => projectCategoriesApi.delete(cid)}
        getItemLabel={(i) => i.category_name}
        buildCreatePayload={(v) => v.category_name}
        buildUpdatePayload={(cid, name) => name}
      />
      <WorkManageEntityModal
        open={subModal}
        onClose={async () => {
          setSubModal(false);
          if (selectedCategory) await loadSubcategories(selectedCategory);
        }}
        title="زیردسته پروژه"
        nameField="category_name"
        nameLabel="نام زیردسته"
        loadItems={async () => {
          if (!selectedCategory) return [];
          const res = await projectSubcategoriesApi.getByCategory(selectedCategory);
          const list = res?.sub_categories || res?.subcategories || res?.data || [];
          setSubcategories(list);
          return list;
        }}
        onCreate={(payload) => projectSubcategoriesApi.create(payload)}
        onUpdate={(sid, name) => projectSubcategoriesApi.update(sid, { category_name: name })}
        onDelete={(sid) => projectSubcategoriesApi.delete(sid)}
        getItemLabel={(i) => i.category_name}
        buildCreatePayload={(v) => ({
          category_id: selectedCategory,
          category_name: v.category_name,
        })}
        buildUpdatePayload={(sid, name) => ({ category_name: name })}
      />
    </WorkFormPage>
  );
};

export default ProjectForm;
